import { useState } from "react";
import { toast } from "sonner";
import { Plus, CheckCircle2, Trash2, Landmark } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader, PageState, StatusPill, Empty } from "@/components/app/Common";
import { ObligationDialog } from "@/pages/Compliance";
import { useLoad } from "@/hooks/useLoad";
import { api } from "@/lib/api";
import { daysLabel, fmtDate } from "@/lib/ui";

const GROUPS = { Canada: ["T2", "GST/HST", "Payroll"], USA: ["Federal Return", "State Return", "Franchise Tax"] };

export default function Tax() {
  const state = useLoad(() => Promise.all([api.get("/taxes"), api.get("/entities"), api.get("/meta")]).then(([a, b, c]) => ({ taxes: a.data, ents: b.data, meta: c.data })));
  const [country, setCountry] = useState("All");
  const [open, setOpen] = useState(false);
  const all = state.data?.taxes || [];
  const rows = all.filter((t) => country === "All" || t.country === country);
  const open_ = all.filter((t) => !t.filed_at);
  const fileIt = async (t) => {
    const r = await api.post(`/taxes/${t.id}/file`);
    toast.success(r.data.next_due ? `Filed. Next due ${fmtDate(r.data.next_due)}` : "Marked as filed");
    state.reload();
  };
  const stats = [
    ["Upcoming tax deadlines", open_.filter((t) => t.days_until >= 0 && t.days_until <= 60).length, "tax-stat-upcoming"],
    ["Overdue", open_.filter((t) => t.status === "Overdue").length, "tax-stat-overdue"],
    ["High late-filing risk", open_.filter((t) => t.late_risk === "High").length, "tax-stat-high-risk"],
    ["Filed", all.filter((t) => t.filed_at).length, "tax-stat-filed"],
  ];
  return (
    <div>
      <PageHeader eyebrow="Module 7" title="Tax tracker" subtitle="T2, GST/HST and payroll in Canada. Federal, state and franchise taxes in the US.">
        <Button onClick={() => setOpen(true)} data-testid="add-tax-btn"><Plus className="h-4 w-4 mr-1" />Add tax deadline</Button>
      </PageHeader>
      <PageState state={state}>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {stats.map(([l, v, id]) => (
            <div key={l} className="surface p-5" data-testid={id}><div className="text-xs text-muted-foreground">{l}</div><div className="font-mono text-3xl font-semibold mt-2">{v}</div></div>
          ))}
        </div>
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          {Object.entries(GROUPS).map(([c, types]) => (
            <div key={c} className="surface p-5" data-testid={`tax-status-${c.toLowerCase()}`}>
              <div className="font-semibold mb-3">{c} · filing status</div>
              {!(state.data?.ents || []).some((e) => e.country === c) ? <p className="text-sm text-muted-foreground">No {c === "USA" ? "US" : "Canadian"} entities.</p> : (
              <div className="grid grid-cols-3 gap-3">
                {types.map((tt) => {
                  const items = open_.filter((t) => t.tax_type === tt && t.country === c);
                  const worst = items.find((t) => t.status === "Overdue") || items.find((t) => t.status === "Due Soon") || items[0];
                  return (
                    <div key={tt} className="rounded-lg border border-border p-3">
                      <div className="text-sm font-medium">{tt}</div>
                      <div className="mt-2">{worst ? <StatusPill status={worst.status} /> : <StatusPill status="Filed" />}</div>
                      <div className="text-xs text-muted-foreground mt-1.5 font-mono">{worst ? fmtDate(worst.due_date) : "Nothing open"}</div>
                    </div>
                  );
                })}
              </div>
              )}
            </div>
          ))}
        </div>
        <div className="flex gap-2 mb-4">
          {["All", "Canada", "USA"].map((c) => (
            <button key={c} onClick={() => setCountry(c)} data-testid={`tax-filter-${c.toLowerCase()}`}
              className={`px-3 py-1.5 text-xs rounded-full border ${country === c ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground"}`}>{c}</button>
          ))}
        </div>
        {rows.length === 0 ? <Empty icon={Landmark} title="No tax deadlines" text="Add an entity to auto-generate tax deadlines, or add one manually." testId="tax-empty" /> : (
          <div className="surface overflow-x-auto" data-testid="tax-table">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground border-b border-border">
                <tr>{["Tax", "Entity", "Period", "Due", "Status", "Late filing risk", ""].map((h) => <th key={h} className="text-left font-medium px-4 py-3">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((t) => (
                  <tr key={t.id} data-testid={`tax-row-${t.id}`}>
                    <td className="px-4 py-3 font-medium">{t.tax_type}</td>
                    <td className="px-4 py-3">{t.entity_name}</td>
                    <td className="px-4 py-3 text-muted-foreground">{t.period}</td>
                    <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">{fmtDate(t.due_date)}{!t.filed_at && <div className="text-muted-foreground">{daysLabel(t.days_until)}</div>}</td>
                    <td className="px-4 py-3"><StatusPill status={t.status} testId={`tax-status-${t.id}`} /></td>
                    <td className="px-4 py-3"><StatusPill status={t.late_risk} /></td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {!t.filed_at && <Button size="sm" variant="outline" onClick={() => fileIt(t)} data-testid={`tax-file-${t.id}`}><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Mark filed</Button>}
                      <Button size="icon" variant="ghost" onClick={async () => { await api.delete(`/taxes/${t.id}`); state.reload(); }} data-testid={`tax-delete-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <ObligationDialog open={open} onOpenChange={setOpen} kind="tax" ents={state.data?.ents} types={state.data?.meta?.tax_types} onSaved={state.reload} />
      </PageState>
    </div>
  );
}
